import React, { useRef, useState, useEffect } from 'react'
import { useTrans } from 'hooks'
import { PaginaNode, VolantinoNode } from '@gql/graphql'
import { Page, PowerBar, TextImageLarge } from 'components/organisms'
import styles from './CategoriaVolantiniTemplate.module.sass'
import { scrollTo } from '@utils/safe'
import { Button, EditableTitle, WContainer } from '@components/atoms'
import { Download } from '@components/icons'
import { url } from 'inspector'
import { useRouter } from 'next/router'

interface Props {
  pagina?: PaginaNode
  volantino?: VolantinoNode
}

const CategoriaVolantiniTemplate = (props: Props) => {
  const { pagina = undefined, volantino = undefined } = props

  const powerBarWrapRef = useRef(null)
  const itemsRef = useRef([])
  const [activeItemId, setActiveItemId] = useState(undefined)

  const powerBarItems = volantino?.sottocategorie?.map((pbi) => ({
    name: pbi.nome,
    slug: pbi.slug,
    selected: pbi.slug === activeItemId,
  }))

  const router = useRouter()
  const t = useTrans()

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 10) {
        if (volantino?.sottocategorie?.length > 0) {
          setActiveItemId(volantino.sottocategorie[volantino.sottocategorie.length - 1].slug)
        }
        return
      }

      setActiveItemId(
        itemsRef?.current
          ?.map((ref, index) => ({
            slug: volantino.sottocategorie[index].slug,
            value:
              ref.getBoundingClientRect().bottom >
              powerBarWrapRef.current?.getBoundingClientRect().bottom,
          }))
          .find((x) => x.value)?.slug
      )
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [itemsRef, powerBarWrapRef, volantino?.sottocategorie])

  const scrollToRef = (item) => {
    const index = volantino?.sottocategorie.findIndex((sc) => sc.slug === item.slug)
    if (typeof index !== 'undefined' && itemsRef?.current?.length > index) {
      scrollTo(
        itemsRef.current[index],
        powerBarWrapRef.current?.getBoundingClientRect().height - 10
      )
    }
  }

  return (
    <Page pagina={pagina} item={volantino}>
      <main className={`${styles.root}`}>
        <TextImageLarge
          item={{
            name: volantino.titolo ?? volantino.intro,
            description: volantino.descrizione,
            image: volantino.immagine,
          }}
          imagePos="right"
          theme="primary"
        />

        <div className={styles.powerBarWrap} ref={powerBarWrapRef}>
          <PowerBar
            items={powerBarItems}
            onItemClick={(item) => scrollToRef(item)}
            className={styles.powerBar}
          />
        </div>

        {volantino?.sottocategorie?.map((sc, i) => (
          <div
            key={sc.slug}
            ref={(el) => {
              itemsRef.current[i] = el
            }}
          >
            <TextImageLarge
              item={{
                name: sc.nome,
                description: sc.descrizioneVolantino,
                children: [
                  {
                    ctaLabel: t('Approfondisci'),
                    url: router.asPath + sc.slug,
                  },
                ],
              }}
              variant="carousel-contained"
              images={sc.immaginiVolantinoDigitale}
              imagePos={i % 2 === 0 ? 'left' : 'right'}
              theme={i % 2 === 0 ? 'light' : 'neutrallight'}
              className={styles.box}
            />
          </div>
        ))}

        {volantino.pdfUrl && (
          <Button
            label={t('Scarica il volantino')}
            iconRight={<Download />}
            className={styles.downloadButtonDesk}
            href={volantino.pdfUrl}
          />
        )}

        <div className={styles.downloadVolatinoMobileSection}>
          <WContainer>
            <EditableTitle
              title={t('Vuoi stampare questo volantino?')}
              className={styles.downloadTitle}
            />
            {volantino.pdfUrl && (
              <Button
                label={t('Scarica il volantino in PDF')}
                variant="secondary"
                iconRight={<Download />}
                className={styles.downloadButtonMobile}
                href={volantino.pdfUrl}
              />
            )}
          </WContainer>
        </div>
      </main>
    </Page>
  )
}

export default CategoriaVolantiniTemplate
